import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  CLOUD_INPUT_DEBOUNCE_MS,
  CLOUD_URL_MIN_LENGTH,
} from '../constants';
import * as Ui from '../styles';

function CloudStorageInput({
  service,
  icon,
  iconAlt,
  label,
  errorMessage,
  onFileLoaded,
  disabled,
  hasError,
}) {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const lastLoadedUrlRef = useRef(null);

  const handleUrlChange = useCallback((e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);

    if (newUrl.length > CLOUD_URL_MIN_LENGTH) {
      setIsValid(service.isValidUrl(newUrl));
    } else {
      setIsValid(null);
    }
  }, [service]);

  const loadFile = useCallback(async (urlToLoad) => {
    if (!urlToLoad || lastLoadedUrlRef.current === urlToLoad) return;

    const isUrlValid = service.isValidUrl(urlToLoad);
    if (!isUrlValid) return;

    lastLoadedUrlRef.current = urlToLoad;

    try {
      setIsLoading(true);
      const { file } = await service.importFromUrl(urlToLoad);

      if (onFileLoaded) {
        onFileLoaded(file);
      }

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      lastLoadedUrlRef.current = null;
      toast.error(error.message || errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [service, onFileLoaded, errorMessage]);

  useEffect(() => {
    if (isValid && url && lastLoadedUrlRef.current !== url) {
      const timeoutId = setTimeout(() => {
        loadFile(url);
      }, CLOUD_INPUT_DEBOUNCE_MS);

      return () => clearTimeout(timeoutId);
    }
  }, [isValid, url, loadFile]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && isValid && !isLoading && !disabled) {
      lastLoadedUrlRef.current = null;
      loadFile(url);
    }
  }, [isValid, isLoading, disabled, loadFile, url]);

  return (
    <Ui.CloudDriveSection hasError={hasError} isValid={isValid}>
      <Ui.CloudDriveIcon>
        <img src={icon} alt={iconAlt} />
      </Ui.CloudDriveIcon>

      <Ui.CloudDriveLabel>{label}</Ui.CloudDriveLabel>

      <Ui.CloudDriveInputWrapper isValid={isValid} hasError={hasError}>
        <Ui.CloudDriveInput
          type="url"
          value={url}
          onChange={handleUrlChange}
          onKeyDown={handleKeyDown}
          placeholder="Cole a URL aqui..."
          disabled={disabled || isLoading}
        />

        {isLoading && (
          <Ui.CloudDriveStatus>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#0D9B9E" strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round"/>
            </svg>
          </Ui.CloudDriveStatus>
        )}

        {!isLoading && isValid !== null && (
          <Ui.CloudDriveStatus isValid={isValid}>
            {isValid ? '\u2713' : '\u2717'}
          </Ui.CloudDriveStatus>
        )}
      </Ui.CloudDriveInputWrapper>
    </Ui.CloudDriveSection>
  );
}

CloudStorageInput.propTypes = {
  service: PropTypes.shape({
    isValidUrl: PropTypes.func.isRequired,
    importFromUrl: PropTypes.func.isRequired,
  }).isRequired,
  icon: PropTypes.string.isRequired,
  iconAlt: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  onFileLoaded: PropTypes.func,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
};

CloudStorageInput.defaultProps = {
  errorMessage: 'Erro ao carregar arquivo.',
  onFileLoaded: null,
  disabled: false,
  hasError: false,
};

export default CloudStorageInput;
